import useTimer from '@/composables/timer/useTimer'
import type { Message } from './frames'
import type { Ref } from 'vue'
import { promiseTimeout } from '@vueuse/core'
import { findLastIndex, propEq } from 'ramda'
import useNotifications from '@/composables/notifications/useNotifications'
import { useI18n } from '@/composables/i18n/useI18n'
import useWidgetSession from '../useWidgetSession'

export { WebSocketConnectionError } from './connection-error'
export type { TroubleshootingInfo } from './connection-error'
export { WEBSOCKET_STATUS } from './transport'

const { pushNotification } = useNotifications()

const useWebsocket = () => {
    const { transport } = useWidgetSession()
    const { store } = transport

    const getMessageIndexByTaskId = (id: string) => {
        return findLastIndex(propEq(id, 'taskId'), store.tasks.value)
    }

    const waitForNewMessage = async (prevMessageId: string) => {
        const { t } = useI18n()
        const { startTimer, resetTimer, time } = useTimer(30)
        startTimer()

        const currentIndex = getMessageIndexByTaskId(prevMessageId)
        let tmp
        let flagLonger = false
        let newMessage: Message | undefined

        while (!newMessage) {
            await promiseTimeout(250)

            // Notifications
            if (time.value % 3 === 0 && tmp !== time.value) {
                tmp = time.value
            }

            if (time.value > 20 && !flagLonger) {
                flagLonger = true
                pushNotification({
                    type: 'warning',
                    subtype: 'wait',
                    content: t('components.notifications.longer_than_expected'),
                })
            }

            newMessage = store.tasks.value.slice(currentIndex).find(t => t.taskId !== prevMessageId)
        }

        resetTimer()
        return newMessage
    }

    const waitForNewMessageByCount = async (taskId: string, previousCount: number) => {
        const { t } = useI18n()
        const { startTimer, resetTimer, time } = useTimer(30)
        startTimer()

        let flagLonger = false
        let newMessage: Message | undefined

        while (!newMessage) {
            await promiseTimeout(250)

            if (time.value > 20 && !flagLonger) {
                flagLonger = true
                pushNotification({
                    type: 'warning',
                    subtype: 'wait',
                    content: t('components.notifications.longer_than_expected'),
                })
            }

            const matching = store.tasksByTaskId(taskId)
            if (matching.length > previousCount) {
                newMessage = matching[matching.length - 1]
            }
        }

        resetTimer()
        return newMessage
    }

    const replaceMessagesByTaskId = (taskId: string, oldCount: number) => {
        store.dropTasks(taskId, oldCount)
    }

    const reset = () => {
        transport.reset()
    }

    if (import.meta.env.DEV) {
        Object.assign(window, { tlWebsocketInstance: transport.websocketInstance })
    }

    return {
        initialize: transport.initialize,
        send: transport.send,
        ping: transport.ping,
        firstMessage: store.firstTask as Readonly<Ref<Message | undefined>>,
        websocketInstance: transport.websocketInstance,
        messages: store.tasks,
        latestMessage: store.latestTask,
        waitForNewMessage,
        waitForNewMessageByCount,
        replaceMessagesByTaskId,
        reset,
        handleWebsocketMessage: transport.handleWebsocketMessage,
    }
}

export default useWebsocket
